import { useState } from 'react';
import { ShoppingListManager, Recipe, ShoppingList } from '@core/shopping';
import { FeedbackCollector } from '@core/feedback';
import { RecipeForm } from './components/RecipeForm';
import { RecipeList } from './components/RecipeList';
import { PantryManager } from './components/PantryManager';
import { ShoppingListView } from './components/ShoppingListView';
import { ParseDemo } from './components/ParseDemo';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { AdminPanel } from './components/AdminPanel';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureGate } from './components/FeatureGate';

function AppContent() {
  const { isAdmin } = useAuth();
  const [manager] = useState(() => new ShoppingListManager());
  const [collector] = useState(() => new FeedbackCollector());
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [shoppingList, setShoppingList] = useState<ShoppingList | null>(null);
  const [activeTab, setActiveTab] = useState<'recipes' | 'pantry' | 'list' | 'demo' | 'analytics' | 'admin'>('recipes');

  const handleAddRecipe = (recipe: Recipe) => {
    manager.addRecipe(recipe);
    setRecipes(manager.getRecipes());
  };

  const handleRemoveRecipe = (recipeId: string) => {
    manager.removeRecipe(recipeId);
    setRecipes(manager.getRecipes());
    // Regenerate shopping list if it exists
    if (shoppingList) {
      setShoppingList(manager.generateShoppingList({ excludePantry: true }));
    }
  };

  const handleGenerateList = () => {
    const list = manager.generateShoppingList({
      excludePantry: true,
      groupByCategory: true
    });
    setShoppingList(list);
    setActiveTab('list');
  };

  const handlePantryUpdate = () => {
    // Regenerate shopping list if it exists
    if (shoppingList) {
      setShoppingList(manager.generateShoppingList({ excludePantry: true }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Heirloom Shopping Lab
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Intelligent ingredient parsing & shopping list consolidation
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Test Coverage</div>
              <div className="text-2xl font-bold text-green-600">326/326</div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('recipes')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'recipes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Recipes ({recipes.length})
            </button>
            <button
              onClick={() => setActiveTab('pantry')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pantry'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pantry ({manager.getPantryItems().length})
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Shopping List {shoppingList && `(${shoppingList.items.length})`}
            </button>
            <button
              onClick={() => setActiveTab('demo')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'demo'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Parser Demo
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Analytics ({collector.getAllEvents().length})
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab('admin')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'admin'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Admin
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'recipes' && (
          <div className="space-y-8">
            <RecipeForm onAddRecipe={handleAddRecipe} />
            <RecipeList
              recipes={recipes}
              onRemoveRecipe={handleRemoveRecipe}
              onGenerateList={handleGenerateList}
            />
          </div>
        )}

        {activeTab === 'pantry' && (
          <PantryManager
            manager={manager}
            onUpdate={handlePantryUpdate}
          />
        )}

        {activeTab === 'list' && shoppingList && (
          <ShoppingListView
            shoppingList={shoppingList}
            manager={manager}
          />
        )}

        {activeTab === 'demo' && (
          <ParseDemo collector={collector} />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsDashboard collector={collector} />
        )}

        {activeTab === 'admin' && (
          <AdminPanel />
        )}

        {activeTab === 'list' && !shoppingList && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No shopping list</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add recipes and click "Generate Shopping List" to get started.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setActiveTab('recipes')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Add Recipes
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <FeatureGate>
        <AppContent />
      </FeatureGate>
    </AuthProvider>
  );
}

export default App;
